import React, { useState, useEffect } from 'react';
import api from './api';

const CITIES = [
  'New York', 'Los Angeles', 'Chicago', 'Boston', 'San Francisco',
  'Seattle', 'Austin', 'Denver', 'Portland', 'Miami',
  'Atlanta', 'Washington DC', 'Philadelphia', 'San Diego', 'Nashville',
  'Minneapolis', 'Charlotte', 'Houston', 'Phoenix', 'Honolulu'
];

const Settings = ({ onBack, user, onProfileUpdate }) => {
  const [raceType, setRaceType] = useState(() => {
    return localStorage.getItem('runrora_race_type') || 'both';
  });
  const [preferredDistance, setPreferredDistance] = useState(() => {
    return localStorage.getItem('runrora_preferred_distance') || 'all';
  });
  const [city, setCity] = useState(user?.city || '');
  const [citySaving, setCitySaving] = useState(false);
  const [digestEnabled, setDigestEnabled] = useState(user?.digest_enabled !== false);
  const [digestSaving, setDigestSaving] = useState(false);
  const [notifPrefs, setNotifPrefs] = useState({
    cheer_enabled: true,
    feed_comment_enabled: true,
    thread_comment_enabled: true,
    follow_enabled: true,
    strategy_share_enabled: true,
  });
  const [notifLoading, setNotifLoading] = useState(false);
  const [savedMessage, setSavedMessage] = useState('');

  const showSaved = (msg = 'Saved') => {
    setSavedMessage(msg);
    setTimeout(() => setSavedMessage(''), 2000);
  };

  const handleRaceTypeChange = (value) => {
    setRaceType(value);
    localStorage.setItem('runrora_race_type', value);
    // Reset distance when switching away from runs
    if (value !== 'runs') {
      setPreferredDistance('all');
      localStorage.setItem('runrora_preferred_distance', 'all');
    }
    showSaved();
  };

  const handleDistanceChange = (value) => {
    setPreferredDistance(value);
    localStorage.setItem('runrora_preferred_distance', value);
    showSaved();
  };

  const handleDigestToggle = async () => {
    if (!user) return;
    const newValue = !digestEnabled;
    setDigestEnabled(newValue);
    setDigestSaving(true);
    try {
      await api.put('/auth/preferences', { digest_enabled: newValue });
      showSaved(newValue ? 'Digest enabled' : 'Digest disabled');
    } catch (err) {
      console.error('Error updating digest preference:', err);
      setDigestEnabled(!newValue); // revert on failure
    } finally {
      setDigestSaving(false);
    }
  };

  const handleCityChange = async (value) => {
    setCity(value);
    if (!user) return;
    setCitySaving(true);
    try {
      const res = await api.put('/auth/profile', {
        first_name: user.first_name,
        last_name: user.last_name,
        profile_photo: user.profile_photo,
        city: value,
      });
      if (onProfileUpdate) onProfileUpdate(res.data);
      showSaved(`Joined ${value} Running`);
    } catch (err) {
      console.error('Error updating city:', err);
    } finally {
      setCitySaving(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    setNotifLoading(true);
    api.get('/notifications/preferences')
      .then(res => setNotifPrefs(res.data))
      .catch(err => console.error('Error loading notification preferences:', err))
      .finally(() => setNotifLoading(false));
  }, [user]);

  const handleNotifToggle = async (key) => {
    if (!user) return;
    const newValue = !notifPrefs[key];
    setNotifPrefs(prev => ({ ...prev, [key]: newValue }));
    try {
      const res = await api.put('/notifications/preferences', { [key]: newValue });
      setNotifPrefs(res.data);
      showSaved(newValue ? 'Enabled' : 'Disabled');
    } catch (err) {
      console.error('Error updating notification preference:', err);
      setNotifPrefs(prev => ({ ...prev, [key]: !newValue }));
    }
  };

  const notifToggleOptions = [
    { key: 'cheer_enabled', label: 'Cheers on my posts', description: 'When someone cheers your feed posts' },
    { key: 'feed_comment_enabled', label: 'Comments on my feed posts', description: 'When someone comments on your feed activity' },
    { key: 'thread_comment_enabled', label: 'Comments on threads I follow', description: 'New replies on threads you are following' },
    { key: 'follow_enabled', label: 'New followers', description: 'When someone starts following you' },
    { key: 'strategy_share_enabled', label: 'Strategy shares', description: 'When someone shares a race strategy with you' },
  ];

  const raceTypeOptions = [
    { key: 'both', label: 'Both' },
    { key: 'runs', label: 'Runs' },
    { key: 'triathlons', label: 'Triathlons' },
  ];

  const distanceOptions = [
    { key: 'all', label: 'All Distances' },
    { key: 'half', label: 'Half Marathon' },
    { key: 'marathon', label: 'Marathon' },
    { key: 'ultra', label: 'Ultramarathon' },
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0B0B10',
      color: '#FFFFFF',
    }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #0B0B10 0%, #16161D 100%)',
        borderBottom: '1px solid rgba(255, 138, 61, 0.2)',
        padding: '1rem 1.5rem',
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}>
        <button
          onClick={onBack}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#FF8A3D',
            fontSize: '1.5rem',
            cursor: 'pointer',
            padding: '0.25rem',
          }}
        >
          ←
        </button>
        <h1 style={{ fontSize: '1.25rem', fontWeight: 700, margin: 0, flex: 1 }}>Settings</h1>
        {savedMessage && (
          <span style={{
            fontSize: '0.85rem',
            color: '#4ade80',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: '0.35rem',
          }}>
            {'✓'} {savedMessage}
          </span>
        )}
      </div>

      <div style={{
        maxWidth: '600px',
        margin: '0 auto',
        padding: '2rem 1.5rem',
      }}>
        {/* Race Type Card */}
        <div style={{
          background: '#16161D',
          borderRadius: '16px',
          padding: '1.5rem',
          marginBottom: '1.5rem',
          border: '1px solid rgba(255, 138, 61, 0.1)',
        }}>
          <h3 style={{
            fontSize: '0.85rem',
            color: '#8b9cb3',
            marginBottom: '1rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}>Race Type</h3>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            {raceTypeOptions.map(option => (
              <button
                key={option.key}
                onClick={() => handleRaceTypeChange(option.key)}
                style={{
                  flex: 1,
                  padding: '0.75rem 1rem',
                  background: raceType === option.key ? '#FF8A3D' : '#0B0B10',
                  border: raceType === option.key
                    ? '1px solid #FF8A3D'
                    : '1px solid rgba(255, 138, 61, 0.2)',
                  color: raceType === option.key ? '#0B0B10' : '#8b9cb3',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  transition: 'all 0.2s ease',
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Preferred Distance Card - only shown for runs */}
        {raceType === 'runs' && (
          <div style={{
            background: '#16161D',
            borderRadius: '16px',
            padding: '1.5rem',
            marginBottom: '1.5rem',
            border: '1px solid rgba(255, 138, 61, 0.1)',
          }}>
            <h3 style={{
              fontSize: '0.85rem',
              color: '#8b9cb3',
              marginBottom: '1rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}>Preferred Distance</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {distanceOptions.map(option => (
                <button
                  key={option.key}
                  onClick={() => handleDistanceChange(option.key)}
                  style={{
                    padding: '0.75rem 1rem',
                    background: preferredDistance === option.key ? 'rgba(255, 138, 61, 0.15)' : 'transparent',
                    border: preferredDistance === option.key
                      ? '1px solid rgba(255, 138, 61, 0.4)'
                      : '1px solid rgba(255, 138, 61, 0.1)',
                    color: preferredDistance === option.key ? '#FF8A3D' : '#8b9cb3',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    textAlign: 'left',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                  }}
                >
                  <div style={{
                    width: '18px',
                    height: '18px',
                    borderRadius: '50%',
                    border: preferredDistance === option.key
                      ? '2px solid #FF8A3D'
                      : '2px solid rgba(139, 156, 179, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    {preferredDistance === option.key && (
                      <div style={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        background: '#FF8A3D',
                      }} />
                    )}
                  </div>
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Your City Card */}
        {user && (
          <div style={{
            background: '#16161D',
            borderRadius: '16px',
            padding: '1.5rem',
            marginBottom: '1.5rem',
            border: '1px solid rgba(255, 138, 61, 0.1)',
          }}>
            <h3 style={{
              fontSize: '0.85rem',
              color: '#8b9cb3',
              marginBottom: '0.5rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}>Your City</h3>
            <p style={{
              fontSize: '0.8rem',
              color: '#6b7280',
              marginBottom: '1rem',
            }}>
              Select your city to join a local running community
            </p>
            <select
              value={city}
              onChange={(e) => handleCityChange(e.target.value)}
              disabled={citySaving}
              style={{
                width: '100%',
                padding: '0.75rem 1rem',
                background: '#0B0B10',
                border: '1px solid rgba(255, 138, 61, 0.2)',
                borderRadius: '10px',
                color: city ? '#FFFFFF' : '#8b9cb3',
                fontSize: '0.9rem',
                cursor: 'pointer',
                outline: 'none',
                appearance: 'none',
                WebkitAppearance: 'none',
              }}
            >
              <option value="">Select a city...</option>
              {CITIES.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            {citySaving && (
              <p style={{ color: '#FF8A3D', fontSize: '0.8rem', marginTop: '0.5rem' }}>
                Saving...
              </p>
            )}
          </div>
        )}

        {/* Email Preferences Card */}
        {user && (
          <div style={{
            background: '#16161D',
            borderRadius: '16px',
            padding: '1.5rem',
            marginBottom: '1.5rem',
            border: '1px solid rgba(255, 138, 61, 0.1)',
          }}>
            <h3 style={{
              fontSize: '0.85rem',
              color: '#8b9cb3',
              marginBottom: '1rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}>Email Notifications</h3>
            <button
              onClick={handleDigestToggle}
              disabled={digestSaving}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0.75rem 1rem',
                background: 'transparent',
                border: '1px solid rgba(255, 138, 61, 0.15)',
                borderRadius: '10px',
                cursor: digestSaving ? 'default' : 'pointer',
                opacity: digestSaving ? 0.6 : 1,
              }}
            >
              <div style={{ textAlign: 'left' }}>
                <div style={{ color: '#FFFFFF', fontSize: '0.9rem', fontWeight: 600 }}>
                  Weekly Race Digest
                </div>
                <div style={{ color: '#8b9cb3', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                  Race countdown and motivation every Sunday
                </div>
              </div>
              <div style={{
                width: '44px',
                height: '24px',
                borderRadius: '12px',
                background: digestEnabled ? '#FF8A3D' : 'rgba(139, 156, 179, 0.3)',
                position: 'relative',
                transition: 'background 0.2s ease',
                flexShrink: 0,
                marginLeft: '1rem',
              }}>
                <div style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  background: '#FFFFFF',
                  position: 'absolute',
                  top: '2px',
                  left: digestEnabled ? '22px' : '2px',
                  transition: 'left 0.2s ease',
                }} />
              </div>
            </button>
          </div>
        )}

        {/* In-App Notifications Card */}
        {user && (
          <div style={{
            background: '#16161D',
            borderRadius: '16px',
            padding: '1.5rem',
            marginBottom: '1.5rem',
            border: '1px solid rgba(255, 138, 61, 0.1)',
            opacity: notifLoading ? 0.6 : 1,
          }}>
            <h3 style={{
              fontSize: '0.85rem',
              color: '#8b9cb3',
              marginBottom: '1rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}>In-App Notifications</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {notifToggleOptions.map(option => (
                <button
                  key={option.key}
                  onClick={() => handleNotifToggle(option.key)}
                  disabled={notifLoading}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0.75rem 1rem',
                    background: 'transparent',
                    border: '1px solid rgba(255, 138, 61, 0.15)',
                    borderRadius: '10px',
                    cursor: notifLoading ? 'default' : 'pointer',
                  }}
                >
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ color: '#FFFFFF', fontSize: '0.9rem', fontWeight: 600 }}>
                      {option.label}
                    </div>
                    <div style={{ color: '#8b9cb3', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                      {option.description}
                    </div>
                  </div>
                  <div style={{
                    width: '44px',
                    height: '24px',
                    borderRadius: '12px',
                    background: notifPrefs[option.key] ? '#FF8A3D' : 'rgba(139, 156, 179, 0.3)',
                    position: 'relative',
                    transition: 'background 0.2s ease',
                    flexShrink: 0,
                    marginLeft: '1rem',
                  }}>
                    <div style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      background: '#FFFFFF',
                      position: 'absolute',
                      top: '2px',
                      left: notifPrefs[option.key] ? '22px' : '2px',
                      transition: 'left 0.2s ease',
                    }} />
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Settings;
